export const testTypes = [
    { 
        value: 'pH', 
        label: 'pH Level', 
        unit: 'pH', 
        category: 'Chemical',
        description: 'Acidity/alkalinity measurement - critical for product stability and mixing compatibility'
    },
    { 
        value: 'viscosity', 
        label: 'Viscosity', 
        unit: 'cP', 
        category: 'Physical',
        description: 'Flow properties and mixing consistency - ensures proper blending and product uniformity'
    },
    { 
        value: 'density', 
        label: 'Density/Specific Gravity', 
        unit: 'g/ml', 
        category: 'Physical',
        description: 'Mass per unit volume - validates mixing ratios and batch consistency'
    },
    { 
        value: 'temperature', 
        label: 'Temperature Control', 
        unit: '°C', 
        category: 'Physical',
        description: 'Temperature monitoring during heating/cooling phases of production'
    },
    { 
        value: 'homogeneity', 
        label: 'Homogeneity/Mixing Uniformity', 
        unit: 'pass/fail', 
        category: 'Physical',
        description: 'Ensures complete mixing and uniform distribution of ingredients throughout batch'
    },
    { 
        value: 'color', 
        label: 'Color Consistency', 
        unit: 'ΔE', 
        category: 'Sensory',
        description: 'Color uniformity during mixing - ensures batch-to-batch consistency'
    },
    { 
        value: 'odor', 
        label: 'Odor Profile', 
        unit: 'scale', 
        category: 'Sensory',
        description: 'Fragrance integration and off-odor detection during production'
    },
    { 
        value: 'appearance', 
        label: 'Visual Inspection', 
        unit: 'pass/fail', 
        category: 'Sensory',
        description: 'Visual assessment for lumps, separation, or mixing defects'
    },
    { 
        value: 'moisture', 
        label: 'Water Content', 
        unit: '%', 
        category: 'Chemical',
        description: 'Moisture content verification - critical for emulsions and stability'
    },
    { 
        value: 'particle_size', 
        label: 'Particle Size Distribution', 
        unit: 'μm', 
        category: 'Physical',
        description: 'Particle uniformity after milling or dispersion processes'
    },
    { 
        value: 'emulsion_stability', 
        label: 'Emulsion Stability', 
        unit: 'min', 
        category: 'Physical',
        description: 'Tests for phase separation or breaking of emulsions during production'
    },
    { 
        value: 'conductivity', 
        label: 'Electrical Conductivity', 
        unit: 'μS/cm', 
        category: 'Chemical',
        description: 'Ionic content measurement - useful for water-based formulations'
    }
];

export const testMethods = {
    'pH': [
        { value: 'pH Meter - Direct Reading', tooltip: 'Electronic probe inserted directly into product - most accurate method' },
        { value: 'pH Strips - Colorimetric', tooltip: 'Disposable test strips that change color - quick field test' },
        { value: 'Digital pH Meter - Calibrated', tooltip: 'Professional laboratory pH meter - calibrated with buffer solutions' },
        { value: 'Benchtop pH Meter', tooltip: 'Standard lab equipment with temperature compensation' }
    ],
    'viscosity': [
        { value: 'Brookfield Viscometer', tooltip: 'Industry standard - rotating spindle measures resistance in product' },
        { value: 'Flow Cup Method', tooltip: 'Time how long product takes to flow through standardized cup' },
        { value: 'Texture Analyzer - Viscosity', tooltip: 'Advanced instrument measures flow properties and texture' },
        { value: 'Visual Flow Assessment', tooltip: 'Manual assessment of how product pours or spreads' }
    ],
    'density': [
        { value: 'Digital Density Meter', tooltip: 'Electronic instrument - most accurate for liquids and creams' },
        { value: 'Pycnometer Method', tooltip: 'Glass bottle with precise volume - weigh empty vs filled' },
        { value: 'Hydrometer Method', tooltip: 'Floating instrument - reads density directly from scale' },
        { value: 'Scale and Volume Method', tooltip: 'Weigh known volume of product - simple calculation' }
    ],
    'microbial': [
        { value: 'Plate Count Method', tooltip: 'Spread sample on agar plates - count bacterial colonies after incubation' },
        { value: 'Rapid Test Strips', tooltip: 'Quick indicator strips for high microbial contamination' },
        { value: 'ATP Bioluminescence', tooltip: 'Light-based test detects living microorganisms instantly' },
        { value: 'Membrane Filtration', tooltip: 'Filter sample through membrane - count organisms under microscope' }
    ],
    'color': [
        { value: 'Visual Color Matching', tooltip: 'Compare product color to approved color standard by eye' },
        { value: 'Spectrophotometer', tooltip: 'Instrument measures exact color values - most precise method' },
        { value: 'Colorimeter', tooltip: 'Handheld device measures color difference from standard' },
        { value: 'Pantone Color Match', tooltip: 'Compare to Pantone color cards - industry standard' }
    ],
    'odor': [
        { value: 'Trained Panel Evaluation', tooltip: 'Multiple trained people smell and rate the product' },
        { value: 'Single Assessor', tooltip: 'One qualified person evaluates odor intensity and quality' },
        { value: 'Hedonic Scale Rating', tooltip: 'Rate from very unpleasant to very pleasant on numbered scale' },
        { value: 'Odor Intensity Scale', tooltip: 'Rate strength from no odor to very strong odor' }
    ],
    'appearance': [
        { value: 'Visual Inspection', tooltip: 'Look at product for defects, separation, particles, etc.' },
        { value: 'Microscopic Examination', tooltip: 'Use microscope to see particles, crystals, contamination' },
        { value: 'Digital Photo Documentation', tooltip: 'Take standardized photos to document appearance' },
        { value: 'Light Box Inspection', tooltip: 'View product under controlled lighting conditions' }
    ],
    'stability': [
        { value: 'Accelerated Aging', tooltip: 'Store at high temperature to simulate long-term aging quickly' },
        { value: 'Freeze-Thaw Cycling', tooltip: 'Repeatedly freeze and thaw to test stability under stress' },
        { value: 'Real-time Stability', tooltip: 'Store under normal conditions and test over months/years' },
        { value: 'Temperature Cycling', tooltip: 'Alternate between hot and cold storage temperatures' }
    ],
    'moisture': [
        { value: 'Loss on Drying', tooltip: 'Heat sample and measure weight loss - simple water content test' },
        { value: 'Karl Fischer Titration', tooltip: 'Chemical reaction method - most accurate for water content' },
        { value: 'Moisture Analyzer', tooltip: 'Infrared heating with automatic weight measurement' },
        { value: 'Oven Drying Method', tooltip: 'Dry in oven at set temperature - weigh before and after' }
    ],
    'particle_size': [
        { value: 'Laser Diffraction', tooltip: 'Laser light scattering - measures particle size distribution' },
        { value: 'Sieve Analysis', tooltip: 'Pass sample through different sized mesh screens' },
        { value: 'Microscopy Measurement', tooltip: 'View under microscope and measure particles manually' },
        { value: 'Dynamic Light Scattering', tooltip: 'Advanced laser method for very small particles' }
    ],
    'temperature': [
        { value: 'Digital Thermometer', tooltip: 'Calibrated digital probe thermometer - accurate temperature reading' },
        { value: 'Infrared Thermometer', tooltip: 'Non-contact temperature measurement - for surface temperatures' },
        { value: 'Thermocouple Probe', tooltip: 'Industrial probe for accurate bulk temperature measurement' },
        { value: 'Temperature Data Logger', tooltip: 'Continuous temperature monitoring and recording device' }
    ],
    'homogeneity': [
        { value: 'Visual Inspection', tooltip: 'Manual visual assessment of mixing uniformity and consistency' },
        { value: 'Sampling Analysis', tooltip: 'Take samples from multiple tank locations and compare properties' },
        { value: 'Conductivity Mapping', tooltip: 'Measure conductivity at different locations to assess mixing' },
        { value: 'Color Uniformity Check', tooltip: 'Compare color consistency across different batch locations' }
    ],
    'emulsion_stability': [
        { value: 'Phase Separation Test', tooltip: 'Visual observation for oil/water phase separation over time' },
        { value: 'Centrifuge Test', tooltip: 'Accelerated stability test using centrifugal force' },
        { value: 'Microscopic Analysis', tooltip: 'Examine emulsion droplet size and distribution under microscope' },
        { value: 'Brookfield Stability Test', tooltip: 'Monitor viscosity changes over time as stability indicator' }
    ],
    'conductivity': [
        { value: 'Conductivity Meter', tooltip: 'Electronic probe measures electrical conductivity of solution' },
        { value: 'TDS Meter', tooltip: 'Total dissolved solids meter - indicates ionic content' },
        { value: 'Inline Conductivity Probe', tooltip: 'Continuous monitoring probe installed in production line' },
        { value: 'Benchtop Conductivity Meter', tooltip: 'Laboratory-grade precision conductivity measurement' }
    ]
};

export const testStandards = {
    'pH': [
        { value: 'USP <791> pH Determination', tooltip: 'US Pharmacopeia standard for pharmaceutical pH testing' },
        { value: 'ASTM E70 - pH Testing', tooltip: 'American Society for Testing Materials - general pH standard' },
        { value: 'ISO 4316 - pH Measurement', tooltip: 'International standard for pH measurement procedures' },
        { value: 'Company SOP-QC-001', tooltip: 'Internal company standard operating procedure for pH testing' }
    ],
    'viscosity': [
        { value: 'USP <912> Viscosity', tooltip: 'US Pharmacopeia standard for pharmaceutical viscosity testing' },
        { value: 'ASTM D2196 - Rheology', tooltip: 'Standard for non-Newtonian viscosity of cosmetics and coatings' },
        { value: 'ISO 2555 - Apparent Viscosity', tooltip: 'International standard for paints and cosmetics viscosity' },
        { value: 'Company SOP-QC-002', tooltip: 'Internal company procedure for viscosity measurement' }
    ],
    'density': [
        { value: 'USP <841> Specific Gravity', tooltip: 'US Pharmacopeia standard for density/specific gravity testing' },
        { value: 'ASTM D1475 - Density', tooltip: 'Standard for density of liquid coatings, paints, and cosmetics' },
        { value: 'ISO 2811 - Density', tooltip: 'International standard for density and specific gravity measurement' },
        { value: 'Company SOP-QC-003', tooltip: 'Internal company procedure for density testing' }
    ],
    'microbial': [
        { value: 'USP <61> Microbial Limits', tooltip: 'US Pharmacopeia standard for microbial enumeration testing' },
        { value: 'USP <62> Microbial ID', tooltip: 'US Pharmacopeia standard for identifying specific microorganisms' },
        { value: 'ISO 21149 - Microbial Count', tooltip: 'International standard for counting aerobic bacteria in cosmetics' },
        { value: 'Company SOP-QC-004', tooltip: 'Internal company procedure for microbial testing' }
    ],
    'color': [
        { value: 'ASTM D2244 - Color Analysis', tooltip: 'Standard for instrumental evaluation of color differences' },
        { value: 'CIE L*a*b* Color Space', tooltip: 'International Commission on Illumination color measurement standard' },
        { value: 'ISO 11664 - Colorimetry', tooltip: 'International standard for colorimetry and color measurement' },
        { value: 'Company SOP-QC-005', tooltip: 'Internal company procedure for color evaluation' }
    ],
    'odor': [
        { value: 'ASTM E544 - Odor Assessment', tooltip: 'Standard practice for evaluating odor in industrial atmospheres' },
        { value: 'ISO 5496 - Sensory Analysis', tooltip: 'International standard for sensory analysis methodology' },
        { value: 'Company SOP-QC-006', tooltip: 'Internal company procedure for odor evaluation' },
        { value: 'Internal Sensory Protocol', tooltip: 'Custom sensory evaluation procedure developed in-house' }
    ],
    'appearance': [
        { value: 'USP <1> Visual Inspection', tooltip: 'US Pharmacopeia general chapter on visual inspection procedures' },
        { value: 'ASTM D1729 - Visual Evaluation', tooltip: 'Standard practice for visual evaluation of color differences' },
        { value: 'Company SOP-QC-007', tooltip: 'Internal company procedure for visual inspection' },
        { value: 'Visual Assessment Protocol', tooltip: 'Standardized visual inspection checklist and procedure' }
    ],
    'stability': [
        { value: 'ICH Q1A Stability Testing', tooltip: 'International Conference on Harmonisation guidelines for stability' },
        { value: 'USP <1150> Stability', tooltip: 'US Pharmacopeia guidance on pharmaceutical stability testing' },
        { value: 'ASTM D1640 - Stability', tooltip: 'Standard for stability testing of cosmetic products' },
        { value: 'Company SOP-QC-008', tooltip: 'Internal company procedure for stability testing' }
    ],
    'moisture': [
        { value: 'USP <921> Water Content', tooltip: 'US Pharmacopeia standard for water determination methods' },
        { value: 'ASTM E203 - Karl Fischer', tooltip: 'Standard for water determination using Karl Fischer titration' },
        { value: 'ISO 760 - Water Determination', tooltip: 'International standard for determining water content' },
        { value: 'Company SOP-QC-009', tooltip: 'Internal company procedure for moisture testing' }
    ],
    'particle_size': [
        { value: 'USP <429> Light Diffraction', tooltip: 'US Pharmacopeia standard for particle size by light diffraction' },
        { value: 'ISO 13320 - Laser Diffraction', tooltip: 'International standard for laser diffraction particle sizing' },
        { value: 'ASTM B822 - Particle Size', tooltip: 'Standard for particle size distribution of metal powders' },
        { value: 'Company SOP-QC-010', tooltip: 'Internal company procedure for particle size analysis' }
    ],
    'temperature': [
        { value: 'ASTM E644 - Temperature Measurement', tooltip: 'Standard test methods for testing industrial resistance thermometers' },
        { value: 'ISO 15175 - Temperature Calibration', tooltip: 'International standard for temperature measurement in food processing' },
        { value: 'Company SOP-QC-011', tooltip: 'Internal company procedure for temperature monitoring' },
        { value: 'NIST Temperature Standards', tooltip: 'National Institute of Standards traceable temperature procedures' }
    ],
    'homogeneity': [
        { value: 'ASTM D2196 - Mixing Assessment', tooltip: 'Standard practice for rheological properties and mixing evaluation' },
        { value: 'ISO 8780 - Mixing Uniformity', tooltip: 'International standard for mixing uniformity in industrial processes' },
        { value: 'Company SOP-QC-012', tooltip: 'Internal company procedure for homogeneity testing' },
        { value: 'FDA Process Validation Guidance', tooltip: 'FDA guidance on mixing and blending validation procedures' }
    ],
    'emulsion_stability': [
        { value: 'ASTM D1849 - Emulsion Stability', tooltip: 'Standard test methods for emulsion stability testing' },
        { value: 'ISO 6721 - Emulsion Properties', tooltip: 'International standard for emulsion characterization' },
        { value: 'Company SOP-QC-013', tooltip: 'Internal company procedure for emulsion stability testing' },
        { value: 'CTFA Guidelines - Emulsions', tooltip: 'Cosmetics industry standards for emulsion testing' }
    ],
    'conductivity': [
        { value: 'ASTM D1125 - Conductivity', tooltip: 'Standard test methods for electrical conductivity of water' },
        { value: 'ISO 7888 - Conductivity Measurement', tooltip: 'International standard for electrical conductivity determination' },
        { value: 'Company SOP-QC-014', tooltip: 'Internal company procedure for conductivity testing' },
        { value: 'USP <645> Conductivity', tooltip: 'US Pharmacopeia standard for conductivity measurement' }
    ]
};

export const testStages = [
    { 
        value: 'pre_mixing', 
        label: 'Pre-Production Setup',
        description: 'Equipment qualification, environmental checks, and setup verification before mixing'
    },
    { 
        value: 'mixing', 
        label: 'In-Process - Mixing/Blending',
        description: 'Quality checks during mixing/blending process - pH, viscosity, homogeneity, temperature'
    },
    { 
        value: 'heating_cooling', 
        label: 'In-Process - Thermal Processing',
        description: 'Temperature-dependent processes - heating, cooling, pasteurization, phase changes'
    },
    { 
        value: 'milling_dispersion', 
        label: 'In-Process - Milling/Dispersion',
        description: 'Particle size reduction, pigment dispersion, and homogenization processes'
    },
    { 
        value: 'batch_completion', 
        label: 'Batch Completion QC',
        description: 'Final bulk product testing after complete production cycle - ready for transfer'
    },
    { 
        value: 'retention', 
        label: 'Retention Sample',
        description: 'Long-term stability and reference sample testing from production batch'
    }
];

export const cleanRoomClasses = [
    { 
        value: 'ISO 5', 
        label: 'ISO 5 (Class 100)',
        description: 'Sterile manufacturing - pharmaceuticals, injectables (≤3,520 particles ≥0.5μm/m³)'
    },
    { 
        value: 'ISO 6', 
        label: 'ISO 6 (Class 1,000)',
        description: 'Aseptic processing - high-end cosmetics, eye products (≤35,200 particles ≥0.5μm/m³)'
    },
    { 
        value: 'ISO 7', 
        label: 'ISO 7 (Class 10,000)',
        description: 'Controlled manufacturing - cosmetics, topicals (≤352,000 particles ≥0.5μm/m³)'
    },
    { 
        value: 'ISO 8', 
        label: 'ISO 8 (Class 100,000)',
        description: 'General manufacturing - standard cosmetics (≤3,520,000 particles ≥0.5μm/m³)'
    },
    { 
        value: 'Controlled Environment', 
        label: 'Controlled Environment',
        description: 'Climate controlled but not classified - HVAC filtered air'
    },
    { 
        value: 'Standard Production', 
        label: 'Standard Production Area',
        description: 'Normal production environment - basic ventilation'
    }
];

export const qcTimelineSteps = [
    { 
        id: 'configuration', 
        title: 'Test Configuration', 
        description: 'Define test type, method, and standards', 
        color: '#2196f3' 
    },
    { 
        id: 'specifications', 
        title: 'Specifications & Targets', 
        description: 'Set target values and acceptance criteria', 
        color: '#ff9800' 
    },
    { 
        id: 'environment', 
        title: 'Environmental & Equipment', 
        description: 'Record conditions and equipment details', 
        color: '#9c27b0' 
    },
    { 
        id: 'sampling', 
        title: 'Sample Documentation', 
        description: 'Sample information and test execution', 
        color: '#607d8b' 
    },
    { 
        id: 'results', 
        title: 'Results & Analysis', 
        description: 'Record test results and analysis', 
        color: '#4caf50' 
    },
    { 
        id: 'review', 
        title: 'QA Review & Approval', 
        description: 'Quality assurance review and final approval', 
        color: '#f44336' 
    }
]; 